import { useEffect, useState } from 'react'
import { Button, FullscreenModal, TextArea } from 'components'
import Flex from 'components/priority/Flex/Flex'
import Text from 'components/priority/Text/Text'
import { useSetToast } from '@/hooks'
import { DESIGNER_SPACE_LABELS } from '@messenger/consts'
import {
  getDesignerProofContext,
  sendDesignerProof,
} from '@/api/designRequests'

const { sendProof } = DESIGNER_SPACE_LABELS

const priceLine = (priceCents, durationMinutes) => {
  const price = `$${(priceCents / 100).toFixed(2)}`
  const duration =
    durationMinutes < 60
      ? `${durationMinutes} mins`
      : (() => {
          const hours = durationMinutes / 60
          const rounded = Number.isInteger(hours)
            ? hours
            : Number(hours.toFixed(1))
          return `${rounded} ${rounded === 1 ? 'hour' : 'hours'}`
        })()
  return `${price} / ${duration}`
}

/**
 * The designer's send-proof dialog: a note for the client, an optional
 * set of suggested add-on services, and Send — which enqueues proof
 * generation; the proof posts to the client's thread once its pages
 * render.
 */
export const SendProofModal = ({
  show: [show, setShow],
  token,
  // The proof-view path passes its just-rendered record so the send
  // reuses it instead of rendering a duplicate.
  reusePdfRecordId,
  onSent,
}) => {
  const setToast = useSetToast()
  const [note, setNote] = useState('')
  const [suggest, setSuggest] = useState(false)
  const [addOns, setAddOns] = useState([])
  const [selected, setSelected] = useState(() => new Set())
  const [sending, setSending] = useState(false)
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    if (!show || !token) return
    setNote('')
    setSuggest(false)
    setSelected(new Set())
    setFailed(false)
    getDesignerProofContext(token)
      // Services the line already carries never show — only what can
      // still be suggested lists.
      .then((data) =>
        setAddOns(data.addOns.filter((addOn) => !addOn.alreadySelected)),
      )
      .catch(() => setAddOns([]))
  }, [show, token])

  const toggleOption = (optionId) => {
    setSelected((current) => {
      const next = new Set(current)
      if (next.has(optionId)) next.delete(optionId)
      else next.add(optionId)
      return next
    })
  }

  const handleSend = async () => {
    setSending(true)
    setFailed(false)
    try {
      await sendDesignerProof({
        token,
        note: note.trim(),
        suggestedOptionIds: suggest ? [...selected] : [],
        reusePdfRecordId,
      })
      setShow(false)
      setToast({
        icon: 'check',
        text: reusePdfRecordId ? sendProof.sentReused : sendProof.sent,
      })
      onSent?.()
    } catch {
      setFailed(true)
    } finally {
      setSending(false)
    }
  }

  return (
    <FullscreenModal show={[show, setShow]}>
      <Flex column padding="32px" width="480px" gap="16px">
        <Text size="20px" weight="600">
          {sendProof.title}
        </Text>

        <Flex column gap="6px">
          <Text size="14px" weight="500">
            {sendProof.noteLabel}
          </Text>
          <TextArea
            value={[note, setNote]}
            rows={5}
            maxLength={4000}
            data-testid="send-proof-note"
          />
          <Text size="12px" color="gray2">
            {sendProof.noteHelper}
          </Text>
        </Flex>

        <label
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            cursor: 'pointer',
          }}
        >
          <input
            type="checkbox"
            checked={suggest}
            onChange={(event) => setSuggest(event.target.checked)}
            data-testid="send-proof-suggest-toggle"
          />
          <Text size="14px" weight="500">
            {sendProof.suggestAddOn}
          </Text>
        </label>

        {suggest && (
          <Flex
            column
            gap="16px"
            padding="16px"
            background="gray5"
            radius="6px"
          >
            {addOns.map((addOn) => (
              <label
                key={addOn.optionId}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '10px',
                  cursor: 'pointer',
                }}
              >
                <input
                  type="checkbox"
                  checked={selected.has(addOn.optionId)}
                  onChange={() => toggleOption(addOn.optionId)}
                  style={{ marginTop: '2px' }}
                />
                <Flex column gap="2px" flex="1">
                  <Flex justify="space-between" gap="12px">
                    <Text size="14px" weight="600">
                      {addOn.label}
                    </Text>
                    <Text size="13px" color="gray1">
                      {priceLine(addOn.priceCents, addOn.durationMinutes)}
                    </Text>
                  </Flex>
                  {addOn.description && (
                    <Text size="12px" color="gray2">
                      {addOn.description}
                    </Text>
                  )}
                </Flex>
              </label>
            ))}
          </Flex>
        )}

        {failed && (
          <Text size="13px" color="error">
            {sendProof.failed}
          </Text>
        )}

        <Flex justify="space-between" margin="8px 0 0 0">
          <Button onClick={() => setShow(false)} disabled={sending}>
            {sendProof.cancel}
          </Button>
          <Button
            primary
            onClick={handleSend}
            disabled={sending || note.trim() === ''}
            data-testid="send-proof-submit"
          >
            {sending ? sendProof.sending : sendProof.send}
          </Button>
        </Flex>
      </Flex>
    </FullscreenModal>
  )
}

export default SendProofModal
